/*
  Copyright (c) 2018-present, Accuracy.
  All rights reserved.
*/
import {createStyles, Dialog, Link, Slide, StyleRules, Theme} from "@material-ui/core";
import makeStyles from '@material-ui/core/styles/makeStyles';
import {TransitionProps} from "@material-ui/core/transitions";
import classNames from 'classnames';
import React, {memo, useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {asset} from "../../utils/asset";

//----------------------------------------------------------------------------------------------------------------------
//- Styles
//----------------------------------------------------------------------------------------------------------------------
export type ExposClassKey = 'root' | 'heading' | 'intro' | 'row' | 'thumb' | 'content' | 'title' | 'subtitle' | 'desc' | 'linkLine' | 'logo' | 'viewer' | 'viewerClose' | 'viewerStage' | 'viewerImg';

const useStyles = makeStyles<Theme, {}, ExposClassKey>(
  (): StyleRules<ExposClassKey> =>
    createStyles({
      root: {
        padding: 'clamp(48px, 10vh, 120px) clamp(20px, 4vw, 56px) clamp(60px, 10vh, 120px)'
      },
      heading: {
        margin: '0 0 clamp(20px, 3vh, 32px)',
        fontFamily: 'var(--font-display)',
        fontSize: 'clamp(30px, 4vw, 56px)',
        letterSpacing: '-.015em'
      },
      intro: {
        margin: '0 0 clamp(28px, 5vh, 48px)',
        maxWidth: '62ch',
        fontSize: '15px',
        lineHeight: 1.85,
        color: 'var(--color-text-body)'
      },
      row: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'clamp(20px, 3vw, 40px)',
        padding: 'clamp(24px, 4vh, 40px) 0',
        borderTop: '1px solid var(--color-rule)',
        '&:last-child': {
          borderBottom: '1px solid var(--color-rule)'
        }
      },
      thumb: {
        flex: '0 0 auto',
        width: '140px',
        height: '210px',
        objectFit: 'cover',
        background: 'var(--color-surface)',
        cursor: 'zoom-in'
      },
      content: {
        flex: '1 1 320px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
      },
      title: {
        margin: 0,
        fontFamily: 'var(--font-display)',
        fontSize: 'clamp(20px, 2.4vw, 28px)',
        lineHeight: 1.2,
        color: 'var(--color-text)'
      },
      subtitle: {
        margin: 0,
        fontSize: '12.5px',
        letterSpacing: '.1em',
        textTransform: 'uppercase',
        color: 'var(--color-text-faint)'
      },
      desc: {
        margin: '6px 0 0',
        fontSize: '14px',
        lineHeight: 1.7,
        color: 'var(--color-text-body)'
      },
      linkLine: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginTop: '4px'
      },
      logo: {
        height: '22px'
      },
      viewer: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, .92)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      },
      viewerClose: {
        position: 'absolute',
        top: '18px',
        right: 'clamp(16px, 3vw, 32px)',
        background: 'none',
        border: '1px solid var(--color-border-soft)',
        color: 'rgba(232,230,226,.7)',
        padding: '8px 16px',
        cursor: 'pointer',
        fontFamily: 'var(--font-body)',
        fontSize: '11px',
        letterSpacing: '.16em',
        textTransform: 'uppercase',
        '&:hover': {
          borderColor: 'var(--color-text-strong)',
          color: 'var(--color-text-strong)'
        }
      },
      viewerStage: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'clamp(40px, 8vh, 80px) clamp(12px, 3vw, 40px)'
      },
      viewerImg: {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain'
      }
    }),
  {
    name: 'Expos'
  });

//----------------------------------------------------------------------------------------------------------------------
//- Component
//----------------------------------------------------------------------------------------------------------------------
export type ExposProps = {
  className?: string;
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default memo(({className}: ExposProps) => {
  // hooks
  const classes = useStyles({});
  const {t} = useTranslation('common');
  const [lightbox, setLightbox] = useState<{img: string, alt: string} | null>(null);

  const openLightbox = (img: string, alt: string) => () => setLightbox({img, alt});
  const closeLightbox = () => setLightbox(null);

  useEffect(() => {
    if (!lightbox) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeLightbox();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!lightbox]);

  return (
    <div className={classNames(classes.root, className)}>
      <h2 className={classes.heading}>{t('topBarMenu.expositions')}</h2>
      <p className={classes.intro}>{t('expos.description')}</p>

      <div className={classes.row}>
        <img className={classes.thumb} src={asset('files/img/Expos/Toulouse_2026.jpg')} alt={t('expos.toulouse2026.alt')} onClick={openLightbox('files/img/Expos/Toulouse_2026.jpg', t('expos.toulouse2026.alt'))}/>
        <div className={classes.content}>
          <h3 className={classes.title}>{t('expos.toulouse2026.title')}</h3>
          <span className={classes.subtitle}>{t('expos.toulouse2026.subtitle')}</span>
          <p className={classes.desc}>{t('expos.toulouse2026.desc')}</p>
        </div>
      </div>

      <div className={classes.row}>
        <img className={classes.thumb} src={asset('files/img/Expos/Montrejeau_2025.jpg')} alt={t('expos.montrejeau2025.alt')} onClick={openLightbox('files/img/Expos/Montrejeau_2025.jpg', t('expos.montrejeau2025.alt'))}/>
        <div className={classes.content}>
          <h3 className={classes.title}>{t('expos.montrejeau2025.title')}</h3>
          <span className={classes.subtitle}>{t('expos.montrejeau2025.subtitle')}</span>
          <p className={classes.desc}>{t('expos.montrejeau2025.desc')}</p>
          <div className={classes.linkLine}>
            <img className={classes.logo} src={asset('files/img/logo_petit_journal.png')} alt="Le Petit Journal"/>
            <Link href={'https://www.lepetitjournal.net/31-haute-garonne/e31c-ht-comminges/2025/07/29/reflets-du-monde-reel-revisite-par-le-photographe-pierre-castay/'}>{t('expos.montrejeau2025.link1')}</Link>
          </div>
          <div className={classes.linkLine}>
            <Link href={'https://www.petiterepublique.com/2025/08/02/montrejeau-une-expo-photos-a-ne-pas-rater-a-espace-andre-marquerie/'}>{t('expos.montrejeau2025.link2')}</Link>
          </div>
        </div>
      </div>

      <div className={classes.row}>
        <img className={classes.thumb} src={asset('files/img/Expos/St_Gaudens_2024.jpg')} alt={t('expos.stGaudens2024.alt')} onClick={openLightbox('files/img/Expos/St_Gaudens_2024.jpg', t('expos.stGaudens2024.alt'))}/>
        <div className={classes.content}>
          <h3 className={classes.title}>{t('expos.stGaudens2024.title')}</h3>
          <span className={classes.subtitle}>{t('expos.stGaudens2024.subtitle')}</span>
          <p className={classes.desc}>{t('expos.stGaudens2024.desc')}</p>
        </div>
      </div>

      <div className={classes.row}>
        <img className={classes.thumb} src={asset('files/img/Expos/St_Gaudens_2022.jpg')} alt={t('expos.stGaudens2022.alt')} onClick={openLightbox('files/img/Expos/St_Gaudens_2022.jpg', t('expos.stGaudens2022.alt'))}/>
        <div className={classes.content}>
          <h3 className={classes.title}>{t('expos.stGaudens2022.title')}</h3>
          <span className={classes.subtitle}>{t('expos.stGaudens2022.subtitle')}</span>
          <p className={classes.desc}>{t('expos.stGaudens2022.desc')}</p>
        </div>
      </div>

      <div className={classes.row}>
        <img className={classes.thumb} src={asset('files/img/Expos/salon-de-la-photo-2018.jpg')} alt={t('expos.salon2018.alt')} onClick={openLightbox('files/img/Expos/salon-de-la-photo-2018.jpg', t('expos.salon2018.alt'))}/>
        <div className={classes.content}>
          <h3 className={classes.title}>{t('expos.salon2018.title')}</h3>
          <span className={classes.subtitle}>{t('expos.salon2018.subtitle')}</span>
          <p className={classes.desc}>{t('expos.salon2018.desc')}</p>
          <div className={classes.linkLine}>
            <Link href={'https://www.lesalondelaphoto.com/'}>{t('expos.salon2018.link1')}</Link>
          </div>
        </div>
      </div>

      <div className={classes.row}>
        <img className={classes.thumb} src={asset('files/img/Expos/St_Gaudens.jpg')} alt={t('expos.stGaudens2017.alt')} onClick={openLightbox('files/img/Expos/St_Gaudens.jpg', t('expos.stGaudens2017.alt'))}/>
        <div className={classes.content}>
          <h3 className={classes.title}>{t('expos.stGaudens2017.title')}</h3>
          <span className={classes.subtitle}>{t('expos.stGaudens2017.subtitle')}</span>
          <p className={classes.desc}>{t('expos.stGaudens2017.desc')}</p>
          <div className={classes.linkLine}>
            <img className={classes.logo} src={asset('files/img/logo_depeche.png')} alt="La Dépêche"/>
            <Link href={'https://www.ladepeche.fr/article/2017/11/03/2677408-noir-blanc-comme-aime-pierre-castay.html'}>{t('expos.stGaudens2017.link1')}</Link>
          </div>
          <div className={classes.linkLine}>
            <img className={classes.logo} src={asset('files/img/logo_petit_journal.png')} alt="Le Petit Journal"/>
            <Link href={'https://www.lepetitjournal.net/31c-comminges/2017/11/06/inauguration-de-lexposition-photos-de-pierre-castay'}>{t('expos.stGaudens2017.link2')}</Link>
          </div>
        </div>
      </div>

      <div className={classes.row}>
        <img className={classes.thumb} src={asset('files/img/Expos/Tournefeuille.jpg')} alt={t('expos.tournefeuille2016.alt')} onClick={openLightbox('files/img/Expos/Tournefeuille.jpg', t('expos.tournefeuille2016.alt'))}/>
        <div className={classes.content}>
          <h3 className={classes.title}>{t('expos.tournefeuille2016.title')}</h3>
          <span className={classes.subtitle}>{t('expos.tournefeuille2016.subtitle')}</span>
          <p className={classes.desc}>{t('expos.tournefeuille2016.desc')}</p>
          <div className={classes.linkLine}>
            <img className={classes.logo} src={asset('files/img/logo_depeche.png')} alt="La Dépêche"/>
            <Link href={'https://www.ladepeche.fr/article/2016/10/21/2443511-la-vie-en-noir-et-blanc-de-pierre-castay.html'}>{t('expos.tournefeuille2016.link1')}</Link>
          </div>
        </div>
      </div>

      <Dialog fullScreen open={!!lightbox} onClose={closeLightbox} TransitionComponent={Transition}>
        {lightbox && (
          <div className={classes.viewer} onClick={closeLightbox}>
            <button className={classes.viewerClose} onClick={closeLightbox}>{t('gallery.close')}</button>
            <div className={classes.viewerStage}>
              <img className={classes.viewerImg} src={asset(lightbox.img)} alt={lightbox.alt}/>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
});
